import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type SalesWindowContext } from '@components/admission-sales-dates';
import { CHANNEL_QUOTA_PER_CHANNEL } from '@components/distribution-section';
import { validateTeamChannels, type ValidationError } from '@components/team-channel-section';
import { type SupportedLanguages } from '@constants/constants.type';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { UserContext } from '@contexts/user/user-context';
import {
  ADMISSION_STATUS_HIDDEN_BY_TRIGGER,
  ADMISSION_STATUS_IN_REVIEW,
  type AdmissionType,
  type Status,
} from '@domain/admission';
import { type EventTeam } from '@domain/eventTeam';
import { type Pass, PASS_TYPE_SEASON } from '@domain/passes';
import { useGetCompanyChannels } from '@hooks/company/use-get-company-channels';
import {
  getTicketsLeftThresholdByType,
  useGetCompanyTicketsLeftThreshold,
} from '@hooks/company/use-get-company-tickets-left-threshold';
import { useGetCompanyVatRates } from '@hooks/company/use-get-company-vat-rates';
import { useGalleryImages } from '@hooks/gallery/use-gallery-images';
import { useGetCompanyPasses } from '@hooks/passes/use-get-company-passes';
import { useGetActiveChannels } from '@hooks/use-active-channels';
import { useGetFormFieldsPresets } from '@hooks/use-form-fields-presets/use-form-fields-presets';
import { filterTreeToSelected } from '@utils/channel-tree';
import moment from 'moment-timezone';

import { seedChannelLimit } from './mappers/from-backend';
import { toPassFormValue } from './mappers/from-pass-backend';
import { diffPassRequest, toCreatePassRequest } from './mappers/to-pass-backend';
import { resolveInitialVatRate } from './mappers/vat-rate';
import { type AdmissionFormValue, type PassFormProps, type SectionKey } from './admission-form.type';
import { ADMISSION_FORM_FLOW_ADMISSION, ADMISSION_FORM_MODE_EDIT, ADMISSION_FORM_TYPE_PASS } from './constants';
import { deriveAdmissionFormEnvironment } from './derive-form-environment';
import {
  ACCESS_CODE_PATTERN,
  buildOfferEditBaseline,
  isOfferSaveDisabled,
  isOfferSeedPending,
  seedOfferFormFields,
  toEventTeamChannel,
} from './offer-form-common';
import { getFormTitle, getSubmitLabel } from './section-header';
import { type UseAdmissionFormResult } from './use-admission-form';
import { isAdmissionFormValid, selectVisibleErrors, validateAdmissionForm, validateChannelLimit } from './validation';
import { getVisibleSections } from './visibility';

const computePassStatusLock = (status: Status | undefined): { blocked: boolean; reasonKey: string } => {
  if (status === ADMISSION_STATUS_HIDDEN_BY_TRIGGER)
    return { blocked: true, reasonKey: 'offers:hiddenByTriggerOfferBlockedChangeStatusMessage' };
  if (status === ADMISSION_STATUS_IN_REVIEW)
    return { blocked: true, reasonKey: 'offers:inReviewOfferBlockedChangeStatusMessage' };
  return { blocked: false, reasonKey: '' };
};

/** State + handlers for the "Start after" trigger-pass selector modal, rendered by `PassForm`. */
export interface StartAfterSelector {
  isOpen: boolean;
  passes: Pass[];
  selectedPassId?: string;
  onSelect: (pass: Pass) => void;
  onClose: () => void;
}

export type UsePassFormResult = UseAdmissionFormResult & { startAfterSelector: StartAfterSelector };

export const usePassForm = ({
  flow,
  mode,
  event,
  pass,
  initialData,
  onSave: onSaveProp,
  onClose: onCloseProp,
  onDirtyChange,
  galleryController: galleryControllerProp,
}: PassFormProps): UsePassFormResult => {
  const { t, i18n } = useTranslation('offers');
  const { clubState, companyState, companyClubState } = useContext(CompanyClubContext);
  const { userState } = useContext(UserContext);
  const { isTicketsLeftThresholdEnabled } = useContext(FeatureFlagContext);

  const mainLanguage = (i18n.language?.split('-')[0] ?? 'en') as SupportedLanguages;
  const timezone = clubState.city.timezone;
  const isClubCountryItaly = clubState.city.country.isoCode === 'ITA';
  const isSuperAdmin = userState?.isSuperAdmin ?? false;
  const hasItalyRegulationContext = isClubCountryItaly;
  const isEventPass = !!event && flow === ADMISSION_FORM_FLOW_ADMISSION;

  const environment = useMemo(
    () =>
      deriveAdmissionFormEnvironment(event, {
        isoCode: clubState.city.country.isoCode,
        currency: clubState.city.country.currency,
      }),
    [event, clubState.city.country.isoCode, clubState.city.country.currency]
  );
  // Company passes never expose SIAE exclusion to organizers (super-admin only) — derived env keeps this false.
  const canExcludeSiaeOffers = environment.canExcludeSiaeOffers;

  const minValidFromDate = useMemo(
    () =>
      environment.eventStartingTime === null
        ? moment.tz(timezone)
        : moment.unix(environment.eventStartingTime).tz(timezone),
    [environment.eventStartingTime, timezone]
  );
  const maxEndDate = useMemo(
    () =>
      environment.eventEndingTime === null
        ? moment.tz(timezone).add(1, 'year')
        : moment.unix(environment.eventEndingTime).tz(timezone),
    [environment.eventEndingTime, timezone]
  );

  const visibleSections = useMemo(
    () =>
      getVisibleSections({
        flow,
        type: ADMISSION_FORM_TYPE_PASS,
        isClubCountryItaly,
        hasItalyRegulationContext,
        isSuperAdmin,
        canExcludeSiaeOffers,
      }),
    [flow, isClubCountryItaly, hasItalyRegulationContext, isSuperAdmin, canExcludeSiaeOffers]
  );

  const defaultValues = useMemo<AdmissionFormValue>(
    () =>
      initialData ??
      toPassFormValue({
        pass,
        mainLanguage,
        timezone,
        now: moment.tz(timezone),
        isEventPass,
        eventStartingTime: environment.eventStartingTime ?? undefined,
        eventEndingTime: environment.eventEndingTime ?? undefined,
        isSuperAdmin,
        canExcludeSiaeOffers,
        hasItalyRegulationContext,
        isTicketsLeftThresholdEnabled,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const methods = useForm<AdmissionFormValue>({ defaultValues, mode: 'onChange' });
  const hasUnsavedChanges = Object.keys(methods.formState.dirtyFields).length > 0;

  useEffect(() => {
    onDirtyChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onDirtyChange]);

  const { data: companyChannels } = useGetCompanyChannels({ companyId: companyState.id });
  const { data: activeChannels, isFetching: isActiveChannelsFetching } = useGetActiveChannels({
    offerId: pass?.id,
    enabled: !!pass?.id,
  });
  const { data: formFieldPresetsData } = useGetFormFieldsPresets();
  const { data: companyVatRates } = useGetCompanyVatRates({
    companyId: companyState.id,
    options: { enabled: !!companyState.id && !isClubCountryItaly },
  });
  const { data: companyTicketsLeftThreshold } = useGetCompanyTicketsLeftThreshold({
    companyId: companyState.id,
    options: { enabled: !!companyState.id },
  });

  const bookingPresets = useMemo(() => formFieldPresetsData?.bookingFormFieldPresets ?? [], [formFieldPresetsData]);

  const passChannelTeam = useMemo<EventTeam | undefined>(
    () => (companyChannels ? { teams: [], channelsWithoutTeams: companyChannels.map(toEventTeamChannel) } : undefined),
    [companyChannels]
  );

  // "Start after" trigger-pass selector (company/preset passes only).
  const { data: companyPasses } = useGetCompanyPasses({
    companyId: companyState.id,
    enabled: !isEventPass && !!companyState.id,
  });
  const triggerPasses = useMemo<Pass[]>(() => companyPasses?.[PASS_TYPE_SEASON] ?? [], [companyPasses]);
  const [isStartAfterOpen, setIsStartAfterOpen] = useState(false);

  const onOpenStartAfter = useCallback(() => setIsStartAfterOpen(true), []);
  const onSelectStartAfter = useCallback(
    (selected: Pass) => {
      const basicInfo = methods.getValues('basicInfo');
      methods.setValue(
        'basicInfo',
        { ...basicInfo, startAfter: { passId: selected.id, displayName: selected.displayName } },
        { shouldDirty: true }
      );
      setIsStartAfterOpen(false);
    },
    [methods]
  );

  // Seeds carry only the trigger pass id — resolve its displayName once the company pass list loads.
  useEffect(() => {
    if (isEventPass || triggerPasses.length === 0) return;
    const current = methods.getValues('basicInfo').startAfter;
    if (!current?.passId || current.displayName) return;
    const match = triggerPasses.find((candidate) => candidate.id === current.passId);
    if (match) {
      methods.setValue(
        'basicInfo.startAfter',
        { passId: current.passId, displayName: match.displayName },
        { shouldDirty: false }
      );
    }
  }, [triggerPasses, isEventPass, methods]);

  const internalGalleryController = useGalleryImages({
    entityId: pass?.id ?? null,
    initialImages: [],
    uploadImage: () => Promise.reject(new Error('Pass gallery persistence is wired in the drawer (B2B-1700)')),
    deleteImage: () => Promise.resolve(),
    reorderImages: () => Promise.resolve(),
  });
  const galleryController = galleryControllerProp ?? internalGalleryController;

  useEffect(() => {
    if (!isClubCountryItaly && pass === null && !initialData && companyVatRates) {
      methods.setValue(
        'vatRate.vatRate',
        resolveInitialVatRate({ companyVatRates, type: ADMISSION_FORM_TYPE_PASS, offer: null }),
        {
          shouldDirty: false,
        }
      );
    }
  }, [companyVatRates, pass, initialData, methods, isClubCountryItaly]);

  useEffect(() => {
    if (
      !passChannelTeam ||
      (pass?.id && (!activeChannels || isActiveChannelsFetching)) ||
      methods.getValues('distribution.channelLimit')
    )
      return;
    methods.setValue('distribution.channelLimit', seedChannelLimit(passChannelTeam, pass, undefined, activeChannels), {
      shouldDirty: false,
    });
  }, [passChannelTeam, activeChannels, isActiveChannelsFetching, pass, methods]);

  const formFieldsSeededRef = useRef(false);
  useEffect(() => {
    if (formFieldsSeededRef.current || initialData || !formFieldPresetsData) return;
    formFieldsSeededRef.current = true;
    methods.setValue('formFields', seedOfferFormFields(pass, bookingPresets, true), { shouldDirty: false });
  }, [formFieldPresetsData, pass, initialData, bookingPresets, methods]);

  const [openSection, setOpenSection] = useState<SectionKey | null>(visibleSections[0] ?? null);
  const toggleSection = useCallback((key: SectionKey) => {
    setOpenSection((prev) => (prev === key ? null : key));
  }, []);

  const watchedValues = methods.watch();
  const allErrors = validateAdmissionForm({
    value: watchedValues,
    type: ADMISSION_FORM_TYPE_PASS,
    isEventPass,
    mainLanguage,
    eventStartingTime: environment.eventStartingTime ?? 0,
    eventEndingTime: environment.eventEndingTime ?? 0,
    timezone,
    t,
  });
  const errors = selectVisibleErrors(allErrors, watchedValues, defaultValues);

  const channelLimit = watchedValues.distribution.channelLimit;
  const channelQuantity = watchedValues.basicInfo.quantity ? Number(watchedValues.basicInfo.quantity) : null;
  const channelErrors: ValidationError[] | undefined =
    channelLimit?.enabled && channelLimit.quotaMode === CHANNEL_QUOTA_PER_CHANNEL
      ? validateTeamChannels(
          filterTreeToSelected(channelLimit.channels, channelLimit.selectedChannelIds),
          channelQuantity
        )
      : undefined;
  const channelLimitError = validateChannelLimit(channelLimit, channelQuantity, t);

  const accessCode = watchedValues.distribution.accessCode;
  const accessCodeError = !!accessCode?.enabled && accessCode.code !== '' && !ACCESS_CODE_PATTERN.test(accessCode.code);

  const isSeedPending =
    isOfferSeedPending(formFieldPresetsData, passChannelTeam, pass, activeChannels) || isActiveChannelsFetching;
  const isSaveDisabled = isOfferSaveDisabled({
    isSeedPending,
    allErrors,
    channelErrors,
    channelLimitError,
    accessCodeError,
    formFields: watchedValues.formFields,
  });

  const buildRequest = useCallback(
    (value: AdmissionFormValue) =>
      toCreatePassRequest({
        value,
        currency: environment.currency,
        mainLanguage,
        companyId: companyState.id,
        timezone,
        pass,
        isEventPass,
        eventStartingTime: environment.eventStartingTime ?? undefined,
        eventEndingTime: environment.eventEndingTime ?? undefined,
        defaultTicketsLeftThreshold: getTicketsLeftThresholdByType(
          companyTicketsLeftThreshold,
          ADMISSION_FORM_TYPE_PASS
        ),
        formFieldPresets: bookingPresets,
        selectedOfferTriggerId: value.basicInfo.startAfter?.passId ?? null,
      }),
    [
      environment.currency,
      mainLanguage,
      environment.eventStartingTime,
      environment.eventEndingTime,
      companyState.id,
      timezone,
      pass,
      isEventPass,
      companyTicketsLeftThreshold,
      bookingPresets,
    ]
  );

  const onSave = useCallback(() => {
    if (isSaveDisabled) return;
    const value = methods.getValues();
    if (
      !isAdmissionFormValid(
        validateAdmissionForm({
          value,
          type: ADMISSION_FORM_TYPE_PASS,
          isEventPass,
          mainLanguage,
          eventStartingTime: environment.eventStartingTime ?? 0,
          eventEndingTime: environment.eventEndingTime ?? 0,
          timezone,
          t,
        })
      )
    ) {
      return;
    }

    const fullPayload = buildRequest(value);
    let payload = fullPayload;

    if (mode === ADMISSION_FORM_MODE_EDIT && pass) {
      const baselineValue = buildOfferEditBaseline({
        defaultValues,
        initialData,
        channelTeam: passChannelTeam,
        offer: pass,
        activeChannels,
        bookingPresets,
        defaultIsNominative: true,
      });
      const isStatusDirty = value.status !== defaultValues.status;
      payload = diffPassRequest(
        fullPayload,
        buildRequest(baselineValue),
        isStatusDirty ? ['salesStatus'] : [],
        isStatusDirty
      );
    }

    void onSaveProp(payload);
  }, [
    isSaveDisabled,
    methods,
    mainLanguage,
    t,
    isEventPass,
    environment.eventStartingTime,
    environment.eventEndingTime,
    timezone,
    buildRequest,
    mode,
    pass,
    defaultValues,
    initialData,
    passChannelTeam,
    activeChannels,
    bookingPresets,
    onSaveProp,
  ]);

  const handleStatusChange = useCallback((newStatus: Status, apply: (status: Status) => void) => apply(newStatus), []);

  const passHasSoldItems = (pass?.soldItems ?? 0) > 0;
  const statusLock = useMemo(() => computePassStatusLock(pass?.status), [pass?.status]);

  const salesWindowContext = useMemo<SalesWindowContext>(
    () => ({
      now: moment.tz(timezone),
      timezone,
      locale: mainLanguage,
      eventId: environment.eventId ?? '',
      eventStartingTime: environment.eventStartingTime ?? 0,
      eventEndingTime: environment.eventEndingTime ?? 0,
      maxEndDate,
      eventIsInThePast: environment.isEventInThePast,
      offerType: ADMISSION_FORM_TYPE_PASS as unknown as AdmissionType,
      offerId: pass?.id,
      selectedOfferTrigger: null,
      setSelectedOfferTrigger: () => undefined,
      onTriggerOfferCancel: () => undefined,
    }),
    [
      timezone,
      mainLanguage,
      environment.eventId,
      environment.eventStartingTime,
      environment.eventEndingTime,
      environment.isEventInThePast,
      maxEndDate,
      pass?.id,
    ]
  );

  return {
    methods,
    title: getFormTitle(t, mode, flow, ADMISSION_FORM_TYPE_PASS),
    submitLabel: getSubmitLabel(t, mode),
    visibleSections,
    openSection,
    toggleSection,
    errors,
    isSaveDisabled,
    hasUnsavedChanges,
    isLoading: false,
    onSave,
    onClose: onCloseProp,
    handleStatusChange,
    currency: environment.currency,
    mainLanguage,
    timezone,
    availableAddOns: [],
    addOnsLoading: false,
    onOpenPresetsModal: () => undefined,
    isAddOnsPresetsModalOpen: false,
    closeAddOnsPresetsModal: () => undefined,
    onAddOnsPresetsSaved: () => undefined,
    eventId: environment.eventId ?? '',
    companyClubId: companyClubState.id,
    addOnsHiddenReason: undefined,
    isAddOnsSectionHidden: true,
    formFieldPresets: bookingPresets,
    isPhoneLockedRequired: false,
    galleryController,
    floorplanImageUrl: undefined,
    salesWindowContext,
    minValidFromDate,
    maxEndDate,
    channelErrors,
    channelLimitError,
    channelLimitLoading: isSeedPending,
    accessCodeError,
    isEventInThePast: environment.isEventInThePast,
    isPricingLocked: passHasSoldItems,
    isDownpaymentLocked: false,
    isFormFieldsLocked: passHasSoldItems,
    isEditingLocked: isEventPass && mode === ADMISSION_FORM_MODE_EDIT,
    statusLock,
    showStatusControl: isEventPass,
    onOpenStartAfter,
    startAfterSelector: {
      isOpen: isStartAfterOpen,
      passes: triggerPasses,
      selectedPassId: pass?.id,
      onSelect: onSelectStartAfter,
      onClose: () => setIsStartAfterOpen(false),
    },
  };
};
